// Strict row type for table data
export interface ProductRow {
  name: string;
  price: number;
  stock: number;
  meta?: { rating?: number };
}
import { Component, Input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { TableColumn, TableVariant, TableSize } from '../models/table.model';
import { LoggerService } from '../services/logger.service';

const tableVariants = cva({
  base: [
    'w-full rounded-xl overflow-hidden',
    'backdrop-blur-xl border',
    'transition-all duration-500 ease-out',
    // Ultra-modern glass base
    'relative',
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-60 before:rounded-xl',
  ].join(' '),
  variants: {
    variant: {
      default: [
        // Light theme
        'bg-white/15 border-white/20',
        'shadow-lg shadow-primary-500/10',
        // Dark theme
        'dark:bg-white/8 dark:border-white/15',
        'dark:shadow-lg dark:shadow-black/20',
      ].join(' '),
      elevated: [
        // Light theme
        'bg-white/20 border-primary-200/30',
        'shadow-xl shadow-primary-500/15',
        // Dark theme
        'dark:bg-white/12 dark:border-primary-400/25',
        'dark:shadow-xl dark:shadow-primary-500/25',
      ].join(' '),
      minimal: [
        // Light theme
        'bg-white/10 border-gray-200/20',
        'shadow-md shadow-gray-500/5',
        // Dark theme
        'dark:bg-white/5 dark:border-gray-600/15',
        'dark:shadow-md dark:shadow-black/10',
      ].join(' '),
      premium: [
        // Ultra-premium for e-commerce
        // Light theme
        'bg-linear-to-br from-white/25 via-white/15 to-primary-50/20',
        'border-2 border-primary-200/40',
        'shadow-2xl shadow-primary-500/20',
        // Dark theme
        'dark:bg-linear-to-br dark:from-white/15 dark:via-white/8 dark:to-primary-900/20',
        'dark:border-2 dark:border-primary-400/30',
        'dark:shadow-2xl dark:shadow-primary-500/30',
      ].join(' '),
    },
    size: {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

const headerVariants = cva({
  base: ['relative backdrop-blur-sm border-b', 'transition-all duration-300 ease-out'].join(' '),
  variants: {
    variant: {
      default: 'bg-white/20 dark:bg-white/10 border-white/20 dark:border-white/15',
      elevated: 'bg-primary-50/30 dark:bg-primary-900/20 border-primary-200/30 dark:border-primary-400/25',
      minimal: 'bg-gray-50/20 dark:bg-gray-800/20 border-gray-200/20 dark:border-gray-600/15',
      premium:
        'bg-linear-to-r from-primary-50/40 to-white/30 dark:from-primary-900/30 dark:to-white/10 border-primary-200/40 dark:border-primary-400/30',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const rowVariants = cva({
  base: [
    'relative transition-all duration-200 ease-out',
    'border-b border-white/10 dark:border-white/5',
    'hover:bg-white/20 dark:hover:bg-white/8',
    'group cursor-pointer',
    // 3D hover effect
    'hover:transform hover:translate-x-1',
    'active:scale-[0.99]',
  ].join(' '),
  variants: {
    variant: {
      default: '',
      striped: 'even:bg-white/5 dark:even:bg-white/3',
      hoverable: 'hover:bg-primary-50/20 dark:hover:bg-primary-900/20',
    },
    selected: {
      true: 'bg-primary-100/30 dark:bg-primary-800/30 border-primary-300/50 dark:border-primary-600/50',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    selected: 'false',
  },
});

@Component({
  selector: 'ui-table-glass',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './table-glass.component.html',
})
export class TableGlassComponent<T extends ProductRow = ProductRow> {
  private _data: T[] = [];
  private _columns: TableColumn<T>[] = [];
  @Input()
  set data(val: T[] | undefined | null) {
    this._data = val ?? [];
  }
  get data(): T[] {
    return this._data;
  }
  @Input()
  set columns(val: TableColumn<T>[] | undefined | null) {
    this._columns = val ?? [];
  }
  get columns(): TableColumn<T>[] {
    return this._columns;
  }
  @Input() variant: TableVariant = 'default';
  @Input() size: TableSize = 'md';
  @Input() sortColumn: string = '';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';
  @Input() trackByFn?: (index: number, item: T) => string | number;
  @Input() emptyMessage: string = 'No data available';
  @Input() striped: boolean = false;
  @Input() hoverable: boolean = true;
  @Input() selectedItems: T[] = [];
  @Input() themeSection: ThemeSection = 'global';

  constructor(private readonly loggerService: LoggerService) {}

  public computedTableClasses = computed(() => {
    return tableVariants({
      variant: this.variant,
      size: this.size,
    });
  });

  public computedHeaderClasses = computed(() => {
    return headerVariants({
      variant: this.variant,
    });
  });

  // Return sorted data by current sortColumn and sortDirection. Implemented as a method
  // to avoid timing issues with signals in unit tests.
  public sortedData(): T[] {
    if (!this.sortColumn) return this.data;

    const sorted = [...this.data].sort((a, b) => {
      const aVal = this.getNestedValue(a, this.sortColumn);
      const bVal = this.getNestedValue(b, this.sortColumn);
      const aIsDefined = typeof aVal === 'number' || typeof aVal === 'string';
      const bIsDefined = typeof bVal === 'number' || typeof bVal === 'string';
      if (!aIsDefined && !bIsDefined) return 0;
      if (!aIsDefined) return 1;
      if (!bIsDefined) return -1;
      if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }

  public getHeaderCellClasses(column: TableColumn<T>): string {
    const baseClasses = 'px-6 py-4 text-left relative group cursor-pointer';
    const alignClasses = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    };
    const sizeClasses = {
      sm: 'px-3 py-2',
      md: 'px-6 py-4',
      lg: 'px-8 py-6',
    };

    return [baseClasses, alignClasses[column.align || 'left'], sizeClasses[this.size], column.headerClass || ''].join(
      ' ',
    );
  }

  public getCellClasses(column: TableColumn<T>, _item: T): string {
    const baseClasses = 'px-6 py-4 relative';
    const alignClasses = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    };
    const sizeClasses = {
      sm: 'px-3 py-2',
      md: 'px-6 py-4',
      lg: 'px-8 py-6',
    };

    return [baseClasses, alignClasses[column.align || 'left'], sizeClasses[this.size], column.cellClass || ''].join(
      ' ',
    );
  }

  public getRowClasses(item: T, _index: number): string {
    const isSelected = this.selectedItems.includes(item);
    const variant = this.striped ? 'striped' : this.hoverable ? 'hoverable' : 'default';

    return rowVariants({
      variant,
      selected: isSelected ? 'true' : 'false',
    });
  }

  public getNestedValue(obj: unknown, path: string): unknown {
    return path.split('.').reduce<unknown>((current, key) => {
      if (current === null || current === undefined) return undefined;
      if (typeof current === 'object' && current !== null) {
        // Use Object.prototype.hasOwnProperty for safety
        if (Object.prototype.hasOwnProperty.call(current, key)) {
          return (current as Record<string, unknown>)[key];
        }
      }
      return undefined;
    }, obj);
  }

  public handleSort(columnKey: string): void {
    if (this.sortColumn === columnKey) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = columnKey;
      this.sortDirection = 'asc';
    }
  }

  public handleRowClick(item: T, index: number): void {
    // Emit row click event or handle selection
    this.loggerService.log('Row clicked:', item, index);
  }
}

/**
 * Usage Examples:
 *
 * Basic e-commerce table:
 * <ui-table-glass
 *   [data]="products"
 *   [columns]="productColumns"
 *   variant="premium"
 *   size="lg"
 * ></ui-table-glass>
 *
 * Shopping cart table:
 * <ui-table-glass
 *   [data]="cartItems"
 *   [columns]="cartColumns"
 *   variant="elevated"
 *   [striped]="true"
 * ></ui-table-glass>
 *
 * Column configuration:
 * productColumns: TableColumn[] = [
 *   { key: 'name', label: 'Product Name', sortable: true },
 *   { key: 'price', label: 'Price', align: 'right', sortable: true },
 *   { key: 'stock', label: 'Stock', align: 'center' },
 *   { key: 'actions', label: 'Actions', template: this.actionsTemplate }
 * ];
 *
 * Custom cell template:
 * <ng-template #actionsTemplate let-item let-column="column" let-index="index">
 *   <div class="flex space-x-2">
 *     <ui-button-glass size="sm" variant="ghost">Edit</button>
 *     <ui-button-glass size="sm" variant="danger">Delete</button>
 *   </div>
 * </ng-template>
 */
